/**
 * TipTap-Editor fuer Antworten, Notizen und Textbausteine (Spec 2 / 7.2).
 *
 * Die Quelle ist immer das TipTap-JSON — HTML und Plaintext erzeugt der Server
 * (`services/richtext.ts`). Der Editor gibt deshalb `TiptapDoc` nach aussen und
 * nie einen HTML-String.
 *
 * Der Vorrat an Knoten deckt sich mit `tiptapDocSchema` aus
 * `@tickethub/shared`: was hier entstehen kann, laesst die Validierung durch.
 */
import { tiptapDocSchema, type TiptapDoc } from '@tickethub/shared';
import StarterKit from '@tiptap/starter-kit';
import { EditorContent, useEditor, type Editor } from '@tiptap/react';
import { Bold, Italic, List, ListOrdered, Quote, Strikethrough } from 'lucide-react';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

type RichTextEditorProps = {
  value: TiptapDoc;
  onChange: (value: TiptapDoc) => void;
  placeholder?: string;
  disabled?: boolean;
  /** Damit die Seite den Editor von aussen fuellen kann (Snippet einfuegen). */
  onReady?: (editor: Editor) => void;
  ariaLabel: string;
};

export function RichTextEditor({
  value,
  onChange,
  disabled = false,
  onReady,
  ariaLabel,
}: RichTextEditorProps) {
  const { t } = useTranslation();

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        // Ueberschriften bleiben auf drei Ebenen — mehr braucht eine Support-Antwort nicht.
        heading: { levels: [1, 2, 3] },
      }),
    ],
    content: value,
    editable: !disabled,
    editorProps: {
      attributes: {
        class: cn(
          'min-h-32 w-full rounded-b-md border border-t-0 border-input bg-background px-3 py-2 text-sm',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
          'prose-sm max-w-none [&_p]:my-1 [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5',
          '[&_blockquote]:border-l-2 [&_blockquote]:pl-3 [&_blockquote]:text-muted-foreground',
        ),
        'aria-label': ariaLabel,
        role: 'textbox',
      },
    },
    onUpdate: ({ editor: instance }) => {
      // Nur gueltige Dokumente nach aussen geben — der Server prueft dasselbe Schema.
      const parsed = tiptapDocSchema.safeParse(instance.getJSON());
      if (parsed.success) onChange(parsed.data as TiptapDoc);
    },
  });

  useEffect(() => {
    if (editor && onReady) onReady(editor);
  }, [editor, onReady]);

  useEffect(() => {
    editor?.setEditable(!disabled);
  }, [editor, disabled]);

  if (!editor) return null;

  const actions = [
    { labelKey: 'bold', mark: 'bold', icon: Bold, run: () => editor.chain().focus().toggleBold().run() },
    { labelKey: 'italic', mark: 'italic', icon: Italic, run: () => editor.chain().focus().toggleItalic().run() },
    {
      labelKey: 'strike',
      mark: 'strike',
      icon: Strikethrough,
      run: () => editor.chain().focus().toggleStrike().run(),
    },
    {
      labelKey: 'bullet_list',
      mark: 'bulletList',
      icon: List,
      run: () => editor.chain().focus().toggleBulletList().run(),
    },
    {
      labelKey: 'ordered_list',
      mark: 'orderedList',
      icon: ListOrdered,
      run: () => editor.chain().focus().toggleOrderedList().run(),
    },
    {
      labelKey: 'quote',
      mark: 'blockquote',
      icon: Quote,
      run: () => editor.chain().focus().toggleBlockquote().run(),
    },
  ] as const;

  return (
    <div>
      <div className="flex flex-wrap gap-1 rounded-t-md border border-input bg-muted/40 p-1">
        {actions.map((action) => (
          <Button
            key={action.labelKey}
            type="button"
            variant={editor.isActive(action.mark) ? 'secondary' : 'ghost'}
            size="icon"
            className="h-7 w-7"
            disabled={disabled}
            aria-label={t(`editor.${action.labelKey}`)}
            title={t(`editor.${action.labelKey}`)}
            onClick={action.run}
          >
            <action.icon aria-hidden="true" />
          </Button>
        ))}
      </div>
      <EditorContent editor={editor} />
    </div>
  );
}
